/**
 * Comment Form Component
 * Add comment form with rich text support
 */

import React, { useState } from 'react';
import { Send, Lock } from 'lucide-react';
import { useCreateComment } from '@/hooks';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';

interface CommentFormProps {
  ticketId: string;
}

export function CommentForm({ ticketId }: CommentFormProps) {
  const [content, setContent] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const { mutate: createComment, isPending } = useCreateComment(ticketId);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim()) return;

    createComment(
      { content, is_internal: isInternal },
      {
        onSuccess: () => {
          setContent('');
          setIsInternal(false);
        },
      }
    );
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Add a comment..."
          rows={4}
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm resize-none"
          disabled={isPending}
        />
      </div>

      <div className="flex items-center justify-between">
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={isInternal}
            onChange={(e) => setIsInternal(e.target.checked)}
            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            disabled={isPending}
          />
          <span className="text-sm text-gray-700 flex items-center">
            <Lock className="h-3 w-3 mr-1" />
            Internal note (not visible to customers)
          </span>
        </label>

        <button
          type="submit"
          disabled={!content.trim() || isPending}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isPending ? (
            <>
              <LoadingSpinner size="sm" className="mr-2" />
              Posting...
            </>
          ) : (
            <>
              <Send className="h-4 w-4 mr-2" />
              Post Comment
            </>
          )}
        </button>
      </div>
    </form>
  );
}
